import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
// Audit dashboard for system administrators

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface DashboardMetrics {
  timestamp: string;
  activeUsers: {
    users: ActiveUser[];
    totalSessions: number;
    uniqueUsers: number;
  };
  workflows: {
    today: {
      total: number;
      completed: number;
      failed: number;
      inProgress: number;
    };
    byType: Record<string, { total: number; completed: number; failed: number; inProgress: number }>;
    byUser: { userId: string; name: string; company: string; count: number }[];
    activeJobs: ActiveJob[];
  };
  aiErrors: {
    todayCount: number;
    errors: AIError[];
  };
  tokenUsage: {
    today: {
      totalTokens: number;
      tokensIn: number;
      tokensOut: number;
      totalRequests: number;
      failedRequests: number;
    };
    yesterday: {
      totalTokens: number;
      totalRequests: number;
    };
    trendPercent: string | null;
  };
  loginActivity: {
    todayLogins: number;
    todayFailedLogins: number;
  };
  activityFeed: ActivityEntry[];
}

interface ActiveUser {
  sessionId: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    company?: { id: string; name: string };
  };
  deviceName: string;
  ipAddress: string;
  location: string | null;
  lastActivity: string;
  sessionStarted: string;
}

interface ActiveJob {
  id: string;
  workflowType: string;
  status: string;
  progress: number;
  progressMessage: string | null;
  user: string;
  startedAt: string | null;
  createdAt: string;
}

interface AIError {
  id: string;
  workflowType: string;
  status: string;
  error: string | null;
  attempts: number;
  user: string;
  timestamp: string;
}

interface ActivityEntry {
  id: string;
  action: string;
  resource: string;
  resourceId: string | null;
  result: 'success' | 'failure';
  severity: string;
  ipAddress: string | null;
  user: string;
  timestamp: string;
}

const REFRESH_INTERVAL = 30000; // 30 seconds

const formatWorkflowType = (type: string): string => {
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const timeAgo = (dateStr: string): string => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m ago`;
};

const AuditDashboard: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'workflows' | 'errors' | 'activity'>('overview');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/dashboard-metrics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`Failed to fetch metrics: ${res.status}`);
      const data = await res.json();
      setMetrics(data);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (user?.role !== 'system_admin') {
      navigate('/');
      return;
    }
    fetchMetrics();
  }, [user, navigate, fetchMetrics]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchMetrics, REFRESH_INTERVAL);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchMetrics]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !metrics) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-6 max-w-md">
          <p className="text-red-600 dark:text-red-400 font-medium">Dashboard Error</p>
          <p className="text-red-500 dark:text-red-300 mt-1 text-sm">{error}</p>
          <button onClick={fetchMetrics} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  const { activeUsers, workflows, aiErrors, tokenUsage, loginActivity, activityFeed } = metrics;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Audit Dashboard</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Real-time system monitoring &middot; Last updated {lastRefresh.toLocaleTimeString()}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {error && (
              <span className="text-xs text-red-500 bg-red-50 dark:bg-red-900/30 px-2 py-1 rounded">
                Refresh failed
              </span>
            )}
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Auto-refresh (30s)
            </label>
            <button
              onClick={fetchMetrics}
              className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors"
            >
              Refresh Now
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 mt-4">
          {(['overview', 'workflows', 'errors', 'activity'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === tab
                  ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-b-2 border-indigo-600'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {tab === 'overview' && 'Overview'}
              {tab === 'workflows' && `Workflows (${workflows.today.total})`}
              {tab === 'errors' && `AI Errors (${aiErrors.todayCount})`}
              {tab === 'activity' && 'Activity Feed'}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                title="Active Users"
                value={activeUsers.uniqueUsers}
                subtitle={`${activeUsers.totalSessions} sessions`}
                color="green"
                pulse={activeUsers.uniqueUsers > 0}
              />
              <StatCard
                title="Workflows Today"
                value={workflows.today.total}
                subtitle={`${workflows.today.completed} completed, ${workflows.today.failed} failed`}
                color="blue"
              />
              <StatCard
                title="AI Errors"
                value={aiErrors.todayCount}
                subtitle={aiErrors.todayCount > 0 ? 'Click Errors tab for details' : 'No errors today'}
                color={aiErrors.todayCount > 0 ? 'red' : 'green'}
              />
              <StatCard
                title="Token Usage"
                value={tokenUsage.today.totalTokens.toLocaleString()}
                subtitle={
                  tokenUsage.trendPercent
                    ? `${parseFloat(tokenUsage.trendPercent) >= 0 ? '+' : ''}${tokenUsage.trendPercent}% vs yesterday`
                    : `${tokenUsage.today.totalRequests} requests`
                }
                color="purple"
              />
            </div>

            {/* Login Activity + Active Jobs Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Login Activity */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-4">
                  Login Activity Today
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-green-600 dark:text-green-400">{loginActivity.todayLogins}</div>
                    <div className="text-sm text-green-700 dark:text-green-300 mt-1">Successful Logins</div>
                  </div>
                  <div className={`rounded-lg p-4 text-center ${
                    loginActivity.todayFailedLogins > 0
                      ? 'bg-red-50 dark:bg-red-900/20'
                      : 'bg-gray-50 dark:bg-gray-700/50'
                  }`}>
                    <div className={`text-3xl font-bold ${
                      loginActivity.todayFailedLogins > 0
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}>{loginActivity.todayFailedLogins}</div>
                    <div className={`text-sm mt-1 ${
                      loginActivity.todayFailedLogins > 0
                        ? 'text-red-700 dark:text-red-300'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>Failed Logins</div>
                  </div>
                </div>
              </div>

              {/* Token Usage Breakdown */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-4">
                  Token Usage Breakdown
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Input Tokens</div>
                    <div className="text-xl font-semibold text-gray-900 dark:text-white">{tokenUsage.today.tokensIn.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Output Tokens</div>
                    <div className="text-xl font-semibold text-gray-900 dark:text-white">{tokenUsage.today.tokensOut.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Total Requests</div>
                    <div className="text-xl font-semibold text-gray-900 dark:text-white">{tokenUsage.today.totalRequests}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Failed Requests</div>
                    <div className={`text-xl font-semibold ${
                      tokenUsage.today.failedRequests > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
                    }`}>{tokenUsage.today.failedRequests}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Active Users Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-4">
                <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" />
                Live Users ({activeUsers.uniqueUsers})
              </h3>
              {activeUsers.users.length === 0 ? (
                <p className="text-gray-400 dark:text-gray-500 text-sm py-4 text-center">No active users in the last 15 minutes</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 dark:border-gray-700">
                        <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">User</th>
                        <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">Company</th>
                        <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">Device</th>
                        <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">IP Address</th>
                        <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">Last Activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeUsers.users.map(session => (
                        <tr key={session.sessionId} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="py-2.5 px-3">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {session.user?.firstName} {session.user?.lastName}
                            </div>
                            <div className="text-xs text-gray-400">{session.user?.email}</div>
                          </td>
                          <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300">
                            {session.user?.company?.name || '—'}
                          </td>
                          <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300">{session.deviceName}</td>
                          <td className="py-2.5 px-3 text-gray-500 dark:text-gray-400 font-mono text-xs">{session.ipAddress}</td>
                          <td className="py-2.5 px-3 text-gray-500 dark:text-gray-400">{timeAgo(session.lastActivity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Active Jobs */}
            {workflows.activeJobs.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-4">
                  Running Workflows ({workflows.activeJobs.length})
                </h3>
                <div className="space-y-3">
                  {workflows.activeJobs.map(job => (
                    <div key={job.id} className="flex items-center gap-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white text-sm">{formatWorkflowType(job.workflowType)}</span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            job.status === 'processing'
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                          }`}>{job.status}</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {job.user} &middot; {job.progressMessage || `${job.progress}% complete`}
                        </div>
                      </div>
                      <div className="w-32">
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div
                            className="bg-indigo-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${job.progress}%` }}
                          />
                        </div>
                        <div className="text-xs text-gray-400 text-right mt-0.5">{job.progress}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Workflows Tab */}
        {activeTab === 'workflows' && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-4 gap-4">
              <StatCard title="Total" value={workflows.today.total} color="blue" />
              <StatCard title="Completed" value={workflows.today.completed} color="green" />
              <StatCard title="Failed" value={workflows.today.failed} color={workflows.today.failed > 0 ? 'red' : 'gray'} />
              <StatCard title="In Progress" value={workflows.today.inProgress} color={workflows.today.inProgress > 0 ? 'yellow' : 'gray'} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* By Type */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-4">
                  By Workflow Type
                </h3>
                {Object.keys(workflows.byType).length === 0 ? (
                  <p className="text-gray-400 dark:text-gray-500 text-sm py-4 text-center">No workflows run today</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(workflows.byType)
                      .sort(([,a], [,b]) => b.total - a.total)
                      .map(([type, stats]) => (
                        <div key={type} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{formatWorkflowType(type)}</span>
                          <div className="flex items-center gap-3 text-xs">
                            <span className="text-gray-500 dark:text-gray-400">{stats.total} total</span>
                            {stats.completed > 0 && <span className="text-green-600 dark:text-green-400">{stats.completed} done</span>}
                            {stats.failed > 0 && <span className="text-red-600 dark:text-red-400">{stats.failed} failed</span>}
                            {stats.inProgress > 0 && <span className="text-blue-600 dark:text-blue-400">{stats.inProgress} running</span>}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* By User */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-4">
                  By User
                </h3>
                {workflows.byUser.length === 0 ? (
                  <p className="text-gray-400 dark:text-gray-500 text-sm py-4 text-center">No workflows run today</p>
                ) : (
                  <div className="space-y-2">
                    {workflows.byUser.map(u => (
                      <div key={u.userId} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                        <div>
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-200">{u.name}</div>
                          <div className="text-xs text-gray-400">{u.company}</div>
                        </div>
                        <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">{u.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Errors Tab */}
        {activeTab === 'errors' && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <StatCard title="AI Errors Today" value={aiErrors.todayCount} color={aiErrors.todayCount > 0 ? 'red' : 'green'} />
              <StatCard title="Failed API Requests" value={tokenUsage.today.failedRequests} color={tokenUsage.today.failedRequests > 0 ? 'red' : 'green'} />
              <StatCard title="Failed Workflows" value={workflows.today.failed} color={workflows.today.failed > 0 ? 'red' : 'green'} />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-4">
                AI / Workflow Errors
              </h3>
              {aiErrors.errors.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-3">&#10003;</div>
                  <p className="text-gray-500 dark:text-gray-400 font-medium">No errors today</p>
                  <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">All workflows and AI calls running smoothly</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {aiErrors.errors.map(err => (
                    <div key={err.id} className="border border-red-100 dark:border-red-800/50 bg-red-50 dark:bg-red-900/10 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 dark:text-white text-sm">{formatWorkflowType(err.workflowType)}</span>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              err.status === 'dead_letter'
                                ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                                : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                            }`}>{err.status}</span>
                            <span className="text-xs text-gray-400">Attempt {err.attempts}</span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {err.user} &middot; {timeAgo(err.timestamp)}
                          </div>
                        </div>
                      </div>
                      {err.error && (
                        <pre className="mt-2 text-xs text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                          {err.error}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Activity Feed Tab */}
        {activeTab === 'activity' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-4">
              Today's Activity ({activityFeed.length} events)
            </h3>
            {activityFeed.length === 0 ? (
              <p className="text-gray-400 dark:text-gray-500 text-sm py-8 text-center">No activity recorded today</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-700">
                      <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">Time</th>
                      <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">User</th>
                      <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">Action</th>
                      <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">Resource</th>
                      <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">Result</th>
                      <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">Severity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityFeed.map(entry => (
                      <tr key={entry.id} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="py-2 px-3 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">
                          {new Date(entry.timestamp).toLocaleTimeString()}
                        </td>
                        <td className="py-2 px-3 text-gray-700 dark:text-gray-200">{entry.user}</td>
                        <td className="py-2 px-3">
                          <span className="text-gray-700 dark:text-gray-200 font-mono text-xs">{entry.action}</span>
                        </td>
                        <td className="py-2 px-3 text-gray-500 dark:text-gray-400">{entry.resource}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            entry.result === 'success'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          }`}>{entry.result}</span>
                        </td>
                        <td className="py-2 px-3">
                          <SeverityBadge severity={entry.severity} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard: React.FC<{
  title: string;
  value: number | string;
  subtitle?: string;
  color: 'green' | 'blue' | 'red' | 'purple' | 'yellow' | 'gray';
  pulse?: boolean;
}> = ({ title, value, subtitle, color, pulse }) => {
  const colorClasses = {
    green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
    gray: 'bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-700'
  };
  const valueClasses = {
    green: 'text-green-700 dark:text-green-400',
    blue: 'text-blue-700 dark:text-blue-400',
    red: 'text-red-700 dark:text-red-400',
    purple: 'text-purple-700 dark:text-purple-400',
    yellow: 'text-yellow-700 dark:text-yellow-400',
    gray: 'text-gray-500 dark:text-gray-400'
  };

  return (
    <div className={`rounded-xl border p-4 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2">
        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{title}</div>
        {pulse && <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
      </div>
      <div className={`text-3xl font-bold mt-1 ${valueClasses[color]}`}>{value}</div>
      {subtitle && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subtitle}</div>}
    </div>
  );
};

const SeverityBadge: React.FC<{ severity: string }> = ({ severity }) => {
  const classes = {
    low: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
    medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
    critical: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
  };
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full ${classes[severity as keyof typeof classes] || classes.low}`}>
      {severity}
    </span>
  );
};

export default AuditDashboard;
